import { useEffect, useSyncExternalStore } from 'react'
import { v4 as uuid } from 'uuid'

export type AgentDomain = 'chart' | 'screener'

export type SurfaceApplyResult = {
    applied: Array<{ op: string; message: string }>
    failed: Array<{ op: string; message: string }>
}

export type SurfaceHandle = {
    domain: AgentDomain
    ready: boolean
    surfaceToken: string
    buildMetadata(): Record<string, unknown>
    applyArtifacts(metadata: Record<string, unknown>): Promise<SurfaceApplyResult | null>
    /** Optional: prepare scratch surface before a screener turn (fork-on-write). Returns false when cancelled. */
    prepareForTurn?: () => Promise<string | null | false>
}

type Listener = () => void

const surfaces = new Map<AgentDomain, SurfaceHandle>()
const listeners = new Set<Listener>()
const surfaceTokens = new Map<AgentDomain, string>()
const domainStableTokens = new Map<AgentDomain, string>()

function emitChange() {
    listeners.forEach((listener) => listener())
}

export function getSurfaceToken(domain: AgentDomain): string | undefined {
    return surfaceTokens.get(domain)
}

export function registerSurface(handle: Omit<SurfaceHandle, 'surfaceToken'> & { surfaceToken?: string }): () => void {
    const token = handle.surfaceToken ?? domainStableTokens.get(handle.domain) ?? uuid()
    domainStableTokens.set(handle.domain, token)
    const fullHandle: SurfaceHandle = { ...handle, surfaceToken: token }
    surfaces.set(handle.domain, fullHandle)
    if (handle.ready) {
        surfaceTokens.set(handle.domain, token)
    } else {
        surfaceTokens.delete(handle.domain)
    }
    emitChange()
    return () => {
        const current = surfaces.get(handle.domain)
        if (current === fullHandle) {
            surfaces.delete(handle.domain)
            domainStableTokens.delete(handle.domain)
            if (surfaceTokens.get(handle.domain) === token) {
                surfaceTokens.delete(handle.domain)
            }
            emitChange()
        }
    }
}

export function getSurface(domain: AgentDomain): SurfaceHandle | undefined {
    return surfaces.get(domain)
}

export function getReadyDomains(): AgentDomain[] {
    return [...surfaces.entries()]
        .filter(([, handle]) => handle.ready)
        .map(([domain]) => domain)
}

export function subscribeSurfaces(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export function getSurfaceSnapshot(): Map<AgentDomain, SurfaceHandle> {
    return surfaces
}

export type WaitForSurfaceOptions = {
    signal?: AbortSignal
    /** Reject if the surface does not reach the awaited state within this many ms (default 30s). */
    timeoutMs?: number
}

function waitForSurfaceState(
    domain: AgentDomain,
    state: 'registered' | 'ready',
    options?: WaitForSurfaceOptions,
): Promise<SurfaceHandle> {
    const satisfied = (handle: SurfaceHandle | undefined): handle is SurfaceHandle =>
        state === 'ready' ? Boolean(handle?.ready) : Boolean(handle)

    const existing = surfaces.get(domain)
    if (satisfied(existing)) {
        return Promise.resolve(existing)
    }

    const timeoutMs = options?.timeoutMs ?? 30_000

    return new Promise((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined

        const onAbort = () => {
            cleanup()
            reject(new DOMException('Surface wait aborted', 'AbortError'))
        }

        const onTimeout = () => {
            cleanup()
            reject(new Error(`Surface ${domain} not ${state} within ${timeoutMs}ms`))
        }

        const tick = () => {
            const handle = surfaces.get(domain)
            if (satisfied(handle)) {
                cleanup()
                resolve(handle)
            }
        }

        const cleanup = () => {
            if (timeoutId !== undefined) clearTimeout(timeoutId)
            unsubscribe()
            options?.signal?.removeEventListener('abort', onAbort)
        }

        if (options?.signal?.aborted) {
            onAbort()
            return
        }
        options?.signal?.addEventListener('abort', onAbort, { once: true })
        timeoutId = setTimeout(onTimeout, timeoutMs)

        const unsubscribe = subscribeSurfaces(tick)
    })
}

/**
 * Resolves once the target surface has mounted, even if it is not usable yet.
 * Callers that need to prepare the surface (screener fork-on-write) must wait for
 * registration first, because `prepareForTurn` is what makes it ready.
 */
export function waitForSurfaceRegistered(
    domain: AgentDomain,
    options?: WaitForSurfaceOptions,
): Promise<SurfaceHandle> {
    return waitForSurfaceState(domain, 'registered', options)
}

/**
 * Resolves when the target surface becomes ready. Rejects on abort (navigation away or user cancel).
 */
export function waitForSurfaceReady(
    domain: AgentDomain,
    options?: WaitForSurfaceOptions,
): Promise<SurfaceHandle> {
    return waitForSurfaceState(domain, 'ready', options)
}

export function useSurfaceRegistry(): {
    surfaces: Map<AgentDomain, SurfaceHandle>
    readyDomains: AgentDomain[]
} {
    const snapshot = useSyncExternalStore(
        subscribeSurfaces,
        getSurfaceSnapshot,
        getSurfaceSnapshot,
    )
    const readyDomains = [...snapshot.entries()]
        .filter(([, handle]) => handle.ready)
        .map(([domain]) => domain)
    return { surfaces: snapshot, readyDomains }
}
